<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, provide, nextTick } from 'vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X, Copy, Trash2, Braces, ChevronDown, ChevronRight, ChevronLeft, GripVertical } from '@lucide/vue';
import Button from '@/components/ui/UIButton.vue';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { aiService } from '@/services/AIService';
import { policyTypeService } from '@/services/PolicyTypeService';
import { subitemTypeService } from '@/services/SubitemTypeService';
import type { CoverageType } from '@/types/PolicyType';
import type { SubitemKind } from '@/types/PolicySublimit';
import type { SubitemType } from '@/types/SubitemType';
import type { PromptField, JsonSchemaField, FieldCategory } from '@/types/PromptField';
import { FIELD_CATEGORIES, CATEGORY_LABELS } from '@/types/PromptField';
import { basicSetup, EditorView } from 'codemirror';
import { linter } from '@codemirror/lint';
import { EditorState, StateField, StateEffect } from '@codemirror/state';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { ViewUpdate, Decoration, type DecorationSet, WidgetType } from '@codemirror/view';
import FieldItem from './FieldItem.vue';
import draggable from 'vuedraggable';

// -----
// Props & Emits
// -----
interface Props {
  modelValue: string; // JSON string of the expected output schema
  disabled?: boolean;
  readonly?: boolean; // When true, hides editing UI and shows only the output
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'update:errorCount', count: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  readonly: false,
});

const emit = defineEmits<Emits>();

// -----
// Constants (make available to template)
// -----
const categoryLabels = CATEGORY_LABELS;
const fieldCategories = FIELD_CATEGORIES;

// -----
// Data
// -----
const loading = ref(false);
const fieldLibrary = ref<PromptField[]>([]);
const selectedFields = ref<JsonSchemaField[]>([]);
const searchTerm = ref('');
const selectedCategory = ref<FieldCategory | ''>('');
const showExamplePayload = ref(false);
const coverageTypes = ref<CoverageType[]>([]);
const showCoverageTypeSelector = ref(false);
const selectedCoverageTypeId = ref<number | ''>('');
const isFieldLibraryCollapsed = ref(false);
const subitemTypes = ref<Record<SubitemKind, SubitemType[]>>({ sublimit: [], subretention: [] });
const activeSubitemSpec = ref<StructuredArraySpec | null>(null);
const selectedSubitemLineId = ref('');
const selectedSubitemTypeId = ref<number | ''>('');

// -----
// Resizable / collapsible panel state
// -----
const PANEL_STATE_KEY = 'promptManager.schemaBuilderPanels.v1';
const MIN_LEFT_PCT = 20;
const MAX_LEFT_PCT = 80;
const DEFAULT_LEFT_PCT = 50;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const loadPanelState = (): { leftPct: number; previewCollapsed: boolean } => {
  try {
    const raw = localStorage.getItem(PANEL_STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const pct = Number(parsed?.leftPct);
      return {
        leftPct: Number.isFinite(pct) ? clamp(pct, MIN_LEFT_PCT, MAX_LEFT_PCT) : DEFAULT_LEFT_PCT,
        previewCollapsed: !!parsed?.previewCollapsed,
      };
    }
  } catch {
    // ignore; fall through to defaults
  }
  return { leftPct: DEFAULT_LEFT_PCT, previewCollapsed: false };
};

const initialPanelState = loadPanelState();
const leftPct = ref(initialPanelState.leftPct);
const previewCollapsed = ref(initialPanelState.previewCollapsed);
const splitContainerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

// Mobile detection — the split-panel UI (divider, collapsed-rail) is desktop-only,
// so we ignore previewCollapsed on mobile and always render both panels stacked.
const isMobileViewport = ref(false);
let mobileMql: MediaQueryList | null = null;
const onMobileChange = (e: MediaQueryListEvent | MediaQueryList) => {
  isMobileViewport.value = e.matches;
};

const savePanelState = () => {
  try {
    localStorage.setItem(PANEL_STATE_KEY, JSON.stringify({ leftPct: leftPct.value, previewCollapsed: previewCollapsed.value }));
  } catch {
    // ignore persistence failures (e.g. storage disabled)
  }
};

watch(previewCollapsed, savePanelState);

const startDrag = (e: PointerEvent) => {
  if (!splitContainerRef.value) return;
  isDragging.value = true;
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'col-resize';
};

const onPointerMove = (e: PointerEvent) => {
  if (!isDragging.value || !splitContainerRef.value) return;
  const rect = splitContainerRef.value.getBoundingClientRect();
  if (rect.width === 0) return;
  const pct = ((e.clientX - rect.left) / rect.width) * 100;
  leftPct.value = clamp(pct, MIN_LEFT_PCT, MAX_LEFT_PCT);
};

const endDrag = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
  savePanelState();
};

const onDividerKey = (e: KeyboardEvent) => {
  if (e.key === 'ArrowLeft') {
    leftPct.value = clamp(leftPct.value - 2, MIN_LEFT_PCT, MAX_LEFT_PCT);
    e.preventDefault();
  } else if (e.key === 'ArrowRight') {
    leftPct.value = clamp(leftPct.value + 2, MIN_LEFT_PCT, MAX_LEFT_PCT);
    e.preventDefault();
  } else if (e.key === 'Home') {
    leftPct.value = MIN_LEFT_PCT;
    e.preventDefault();
  } else if (e.key === 'End') {
    leftPct.value = MAX_LEFT_PCT;
    e.preventDefault();
  } else {
    return;
  }
  savePanelState();
};

const resetSplit = () => {
  leftPct.value = DEFAULT_LEFT_PCT;
  savePanelState();
};

// CodeMirror editor refs
const editorRef = ref<HTMLElement | null>(null);
const editorView = ref<EditorView | null>(null);

// Sync state
const isSyncingFromEditor = ref(false); // Flag to prevent circular updates
let lastEmittedSchema = ''; // Track last emitted schema to prevent self-echo loops
const syncError = ref<string | null>(null);
let editorUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

// Flag to track programmatic updates to CodeMirror
const isProgrammaticUpdate = ref(false);

// -----
// Computed
// -----
const filteredFields = computed(() => {
  let fields = fieldLibrary.value;

  // Filter by category
  if (selectedCategory.value) {
    fields = fields.filter((field) => field.category === selectedCategory.value);
  }

  // Filter by search term
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    fields = fields.filter(
      (field) =>
        field.displayName.toLowerCase().includes(term) ||
        field.description.toLowerCase().includes(term) ||
        field.fieldName.toLowerCase().includes(term),
    );
  }

  return fields;
});

const fieldsByCategory = computed(() => {
  const groups = new Map<string, PromptField[]>();

  filteredFields.value.forEach((field) => {
    const category = field.category || 'other';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(field);
  });

  // Sort groups by category order
  const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => {
    const aOrder = Object.values(fieldCategories).indexOf(a as FieldCategory);
    const bOrder = Object.values(fieldCategories).indexOf(b as FieldCategory);
    return aOrder - bOrder;
  });

  return new Map(sortedEntries);
});

// Marker key injected into generation so we can identify each object's field.id.
// Stripped from the final output before it's shown to the user or emitted.
const FIELD_ID_MARKER = '__fieldId__';

const COVERAGE_LINE_ITEM_NAME = '_coverageLineItem';

// Categories whose fields live inside a repeatable array item rather than at the level
// the author clicked. Both belong to a specific PolicyLine, so a typed item always nests
// under the coverage line whose CoverageType scopes its type list.
interface StructuredArraySpec {
  category: string;
  kind: SubitemKind;
  arrayName: string;
  itemName: string;
  itemLabel: string;
  arrayDescription: string;
  typeIdKey: 'sublimitTypeId' | 'subretentionTypeId';
  typeNameKey: 'sublimitTypeName' | 'subretentionTypeName';
  itemDescriptionFor: (typeName: string) => string;
}

const STRUCTURED_ARRAYS: Record<string, StructuredArraySpec> = {
  sublimits: {
    category: 'sublimits',
    kind: 'sublimit',
    arrayName: 'sublimits',
    itemName: '_sublimitItem',
    itemLabel: 'sublimit',
    arrayDescription: 'Sublimits applying to this coverage',
    typeIdKey: 'sublimitTypeId',
    typeNameKey: 'sublimitTypeName',
    itemDescriptionFor: (typeName) => `Sublimit: ${typeName}`,
  },
  subretentions: {
    category: 'subretentions',
    kind: 'subretention',
    arrayName: 'subretentions',
    itemName: '_subretentionItem',
    itemLabel: 'subretention',
    arrayDescription: 'Subretentions applying to this coverage',
    typeIdKey: 'subretentionTypeId',
    typeNameKey: 'subretentionTypeName',
    itemDescriptionFor: (typeName) => `Subretention: ${typeName}`,
  },
};

const structuredSpecs = Object.values(STRUCTURED_ARRAYS);

const specForCategory = (category: string): StructuredArraySpec | undefined => STRUCTURED_ARRAYS[category];

const specForArrayName = (arrayName: string): StructuredArraySpec | undefined =>
  structuredSpecs.find((spec) => spec.arrayName === arrayName);

// An array's item node carries the shape of a single element. It stays in the schema
// rather than being unwrapped, because the API reads properties[0] as the item template.
const itemNodeNameFor = (arrayName: string): string => {
  if (arrayName === 'coverageLines') return COVERAGE_LINE_ITEM_NAME;
  return specForArrayName(arrayName)?.itemName ?? '_item';
};

const isItemNode = (field: { name: string; type: string }): boolean => field.name.startsWith('_') && field.type === 'object';

const ITEM_TYPE_KEYS = [
  'coverageTypeId',
  'coverageTypeName',
  'sublimitTypeId',
  'sublimitTypeName',
  'subretentionTypeId',
  'subretentionTypeName',
] as const;

const isTypedItem = (field: Pick<JsonSchemaField, 'coverageTypeId' | 'sublimitTypeId' | 'subretentionTypeId'>): boolean =>
  !!(field.coverageTypeId || field.sublimitTypeId || field.subretentionTypeId);

const copyItemTypeKeys = (target: Record<string, any>, source: Record<string, any>) => {
  ITEM_TYPE_KEYS.forEach((key) => {
    if (source[key] != null && source[key] !== '') target[key] = source[key];
  });
};

const defaultItemDescription = (itemDef: any): string => {
  if (itemDef.coverageTypeName) return `Coverage line: ${itemDef.coverageTypeName}`;
  if (itemDef.sublimitTypeName) return STRUCTURED_ARRAYS.sublimits.itemDescriptionFor(itemDef.sublimitTypeName);
  if (itemDef.subretentionTypeName) return STRUCTURED_ARRAYS.subretentions.itemDescriptionFor(itemDef.subretentionTypeName);
  return 'Array item object structure';
};

// Incoming JSON is hand-editable, so an item node may arrive without a declared type.
// Treating it as a leaf would flatten the array and discard the item's fields.
const isItemDescriptor = (descriptor: any): boolean =>
  !!descriptor && typeof descriptor.name === 'string' && descriptor.name.startsWith('_') && (descriptor.type === 'object' || descriptor.type == null);

// Generate JSON schema from selected fields (array-based format)
const generateJsonSchema = (): string => {
  if (selectedFields.value.length === 0) {
    return '';
  }

  const schema: any[] = [];

  // Only process root fields (fields with no parentId)
  const rootFields = getRootFields(selectedFields.value);
  rootFields.forEach((field) => {
    schema.push(buildFieldDefinition(field));
  });

  return JSON.stringify(schema, null, 2);
};

// Raw JSON (with __fieldId__ markers intact) — internal only, used for line mapping.
const rawGeneratedJsonSchema = computed(() => generateJsonSchema());

// Computed version for template usage (strips field id markers)
const generatedJsonSchema = computed(() => stripFieldIdMarkers(rawGeneratedJsonSchema.value));

// Strip __fieldId__ marker lines from the JSON output and fix trailing commas.
const stripFieldIdMarkers = (jsonStr: string): string => {
  if (!jsonStr) return jsonStr;
  // Remove each marker line along with any trailing comma on the previous line it causes.
  // The marker is always followed by a comma if more properties follow.
  // Match: optional leading whitespace + "__fieldId__": "...", + \n
  // OR: optional leading whitespace + "__fieldId__": "..." (last property) + \n
  return jsonStr.replace(/^[ \t]*"__fieldId__":\s*"[^"]*",?\n/gm, '');
};

// Build field definition for JSON schema generation (array-based format)
const buildFieldDefinition = (field: JsonSchemaField): Record<string, any> => {
  const fieldDefinition: Record<string, any> = {
    [FIELD_ID_MARKER]: field.id,
    name: field.name,
    type: field.type,
    description: field.description,
    required: field.required,
  };

  if (field.verificationLabel) {
    fieldDefinition.verificationLabel = field.verificationLabel;
  }

  // Handle nested objects - check if this field has children
  const children = getChildrenOf(field.id, selectedFields.value);

  if (field.type === 'object' && children.length > 0) {
    fieldDefinition.properties = [];
    children.forEach((child) => {
      if (child.name.startsWith('_')) {
        // Unwrap placeholder: include its children instead of dropping it
        const placeholderChildren = getChildrenOf(child.id, selectedFields.value);
        placeholderChildren.forEach((grandChild) => {
          if (!grandChild.name.startsWith('_')) {
            fieldDefinition.properties.push(buildFieldDefinition(grandChild));
          }
        });
      } else {
        fieldDefinition.properties.push(buildFieldDefinition(child));
      }
    });
  }

  // Handle array items - the item node is serialized, never unwrapped: the API reads an
  // array descriptor's properties[0] as the item template and drops everything after it.
  if (field.type === 'array' && children.length > 0) {
    // Typed items take precedence: the API's union gate requires every child to carry a
    // type name, so an untyped sibling would collapse the array to one item.
    const typedItems = children.filter(isTypedItem);
    const itemNodes = typedItems.length > 0 ? typedItems : children.filter((c) => isItemNode(c));

    if (itemNodes.length > 0) {
      fieldDefinition.properties = itemNodes.map((item) => {
        const itemDef: Record<string, any> = {
          [FIELD_ID_MARKER]: item.id,
          name: item.name,
          type: 'object',
          description: item.description,
          required: item.required,
        };
        if (item.verificationLabel) {
          itemDef.verificationLabel = item.verificationLabel;
        }
        copyItemTypeKeys(itemDef, item);
        const itemChildren = getChildrenOf(item.id, selectedFields.value).filter((c) => !c.name.startsWith('_'));
        if (itemChildren.length > 0) {
          itemDef.properties = itemChildren.map((c) => buildFieldDefinition(c));
        }
        return itemDef;
      });
    } else {
      fieldDefinition.properties = children.filter((c) => !c.name.startsWith('_')).map((c) => buildFieldDefinition(c));
    }
  }

  return fieldDefinition;
};

// -----
// Field ↔ JSON Line Mapping
// -----
const fieldLineMap = ref(new Map<string, { line: number; charPos: number; endCharPos: number }>());
const lineFieldMap = ref(new Map<number, string>());

// Build field ↔ line maps from the raw JSON (with __fieldId__ markers intact).
// Each field's id is embedded in the JSON so mapping is exact — no name heuristics.
const buildFieldLineMaps = (rawJsonStr: string) => {
  const fieldToLine = new Map<string, { line: number; charPos: number; endCharPos: number }>();
  const lineToField = new Map<number, string>();

  if (!rawJsonStr) return { fieldToLine, lineToField };

  const markerRegex = /^([ \t]*)"__fieldId__":\s*"([^"]+)"/gm;

  // Find all marker lines with their positions (positions refer to the ORIGINAL raw string).
  const markers: Array<{ fieldId: string; rawLineStart: number; rawLineIdx: number }> = [];

  const rawLines = rawJsonStr.split('\n');
  const rawLineOffsets: number[] = [];
  {
    let o = 0;
    for (const l of rawLines) {
      rawLineOffsets.push(o);
      o += l.length + 1;
    }
  }

  let m: RegExpExecArray | null;
  while ((m = markerRegex.exec(rawJsonStr)) !== null) {
    const fieldId = m[2];
    const lineStartInRaw = m.index;
    // Determine which line this is
    let rawLineIdx = 0;
    for (let i = 0; i < rawLineOffsets.length; i++) {
      if (rawLineOffsets[i] === lineStartInRaw) {
        rawLineIdx = i;
        break;
      }
    }
    markers.push({ fieldId, rawLineStart: lineStartInRaw, rawLineIdx });
  }

  if (markers.length === 0) return { fieldToLine, lineToField };

  // Compute the stripped output's line positions.
  // Each marker line gets removed, so lines after a marker shift up by 1.
  // For each marker, the containing object's `{` is on the line just before the marker.
  // After stripping, that `{` line keeps its index (strip is below it), but the marker's
  // former line position now belongs to what was the line AFTER the marker.

  // Map raw line index -> stripped line index for every line.
  const rawToStripped: number[] = new Array(rawLines.length).fill(-1);
  const markerRawIndexSet = new Set(markers.map((m) => m.rawLineIdx));
  let strippedIdx = 0;
  for (let i = 0; i < rawLines.length; i++) {
    if (markerRawIndexSet.has(i)) {
      rawToStripped[i] = -1; // removed
    } else {
      rawToStripped[i] = strippedIdx++;
    }
  }

  // Build the stripped lines array and char offsets for final positions.
  const strippedLines = rawLines.filter((_, i) => !markerRawIndexSet.has(i));
  const strippedLineOffsets: number[] = [];
  {
    let o = 0;
    for (const l of strippedLines) {
      strippedLineOffsets.push(o);
      o += l.length + 1;
    }
  }

  // String-aware brace matcher operating on stripped lines.
  const findClosingBrace = (openLineIdx: number): number => {
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = openLineIdx; i < strippedLines.length; i++) {
      const line = strippedLines[i];
      for (let k = 0; k < line.length; k++) {
        const ch = line[k];
        if (escaped) {
          escaped = false;
          continue;
        }
        if (inString) {
          if (ch === '\\') escaped = true;
          else if (ch === '"') inString = false;
          continue;
        }
        if (ch === '"') inString = true;
        else if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) return strippedLineOffsets[i] + k + 1;
        }
      }
    }
    return strippedLineOffsets[openLineIdx] + strippedLines[openLineIdx].length;
  };

  // For each marker, the enclosing `{` is the nearest `{`-ending line before the marker.
  // In the stripped output, that `{` line's index is rawToStripped[markerRawIndex - 1]
  // (or earlier if the line above doesn't end with `{`).
  for (const marker of markers) {
    let braceRawIdx = -1;
    for (let j = marker.rawLineIdx - 1; j >= 0; j--) {
      if (markerRawIndexSet.has(j)) continue;
      if (rawLines[j].trimEnd().endsWith('{')) {
        braceRawIdx = j;
        break;
      }
    }
    if (braceRawIdx < 0) continue;

    const braceStrippedIdx = rawToStripped[braceRawIdx];
    if (braceStrippedIdx < 0) continue;

    // The "name" line in the stripped output is the line immediately after the brace line
    const nameStrippedIdx = braceStrippedIdx + 1;

    const charPos = strippedLineOffsets[braceStrippedIdx];
    const endCharPos = findClosingBrace(braceStrippedIdx);

    fieldToLine.set(marker.fieldId, { line: braceStrippedIdx, charPos, endCharPos });
    if (nameStrippedIdx < strippedLines.length) {
      lineToField.set(nameStrippedIdx, marker.fieldId);
    }
  }

  return { fieldToLine, lineToField };
};

const categoryOptions = computed(() => {
  const categories = [...new Set(fieldLibrary.value.map((f) => f.category))];
  return categories.map((cat) => ({
    value: cat,
    label: categoryLabels[cat as FieldCategory] || cat,
  }));
});

// Static field type options (doesn't need to be computed)
const fieldTypeOptions = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
];

// Computed property for visible root fields (excluding generic placeholders, but keeping item nodes)
const visibleRootFields = computed(() => {
  return getRootFields(selectedFields.value).filter((f) => !f.name.startsWith('_') || isItemNode(f));
});

// Computed properties for UISelect options
const categorySelectOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'All Categories' }];

  for (const category of categoryOptions.value) {
    options.push({
      value: category.value,
      label: category.label,
    });
  }

  return options;
});

// No need for fieldHierarchy - just use selectedFields directly since they have children arrays

// Always show all fields expanded (no collapse/expand logic)
// Helper functions for coverage line structure
const getCoverageLinesArrayField = (): JsonSchemaField | undefined => {
  return selectedFields.value.find((field) => field.name === 'coverageLines' && !field.parentId);
};

const getCoverageLineItemField = (): JsonSchemaField | undefined => {
  const arrayField = getCoverageLinesArrayField();
  if (!arrayField) {
    return undefined;
  }
  return selectedFields.value.find((field) => field.parentId === arrayField.id && field.name === '_coverageLineItem');
};

const ensureCoverageLinesArray = (): JsonSchemaField => {
  let arrayField = getCoverageLinesArrayField();
  if (!arrayField) {
    arrayField = {
      id: generateFieldId(),
      name: 'coverageLines',
      description: 'Array of coverage lines for the policy',
      type: 'array',
      required: false,
    };
    selectedFields.value = [...selectedFields.value, arrayField];
  }
  return arrayField;
};

const ensureCoverageLineStructure = (): { arrayField: JsonSchemaField; itemObject: JsonSchemaField } => {
  const arrayField = ensureCoverageLinesArray();
  let itemObject: JsonSchemaField | undefined;

  // Check for existing itemObject
  itemObject = selectedFields.value.find((field) => field.parentId === arrayField.id && field.name === '_coverageLineItem');

  if (!itemObject) {
    itemObject = {
      id: generateFieldId(),
      name: '_coverageLineItem',
      description: 'Coverage line object structure',
      type: 'object',
      required: false,
      parentId: arrayField.id,
    };
    selectedFields.value = [...selectedFields.value, itemObject];
  }

  return { arrayField, itemObject };
};

// Get all _coverageLineItem objects under the coverageLines array
const getCoverageLineItems = (): JsonSchemaField[] => {
  const arrayField = getCoverageLinesArrayField();
  if (!arrayField) return [];
  return selectedFields.value.filter((f) => f.parentId === arrayField.id && (f.name === '_coverageLineItem' || f.coverageTypeId));
};

// Get coverage type IDs already used by existing _coverageLineItem objects
const usedCoverageTypeIds = computed(() => {
  return new Set(
    getCoverageLineItems()
      .filter((f) => f.coverageTypeId)
      .map((f) => f.coverageTypeId),
  );
});

// Coverage types available for selection (not already used)
const availableCoverageTypes = computed(() => {
  return coverageTypes.value.filter((ct) => !usedCoverageTypeIds.value.has(ct.coverageTypeId));
});

// Add a coverage line item for a specific coverage type with all standard line fields
const addCoverageLineForType = (ctId: number, ctName: string) => {
  const arrayField = ensureCoverageLinesArray();
  const coverageLineFields = fieldLibrary.value.filter((f) => f.category === 'coverageLines' && f.isActive);

  const itemObjectId = generateFieldId();
  const itemObject: JsonSchemaField = {
    id: itemObjectId,
    name: '_coverageLineItem',
    description: `Coverage line: ${ctName}`,
    type: 'object',
    required: false,
    parentId: arrayField.id,
    coverageTypeId: ctId,
    coverageTypeName: ctName,
  };

  const childFields: JsonSchemaField[] = coverageLineFields.map((libraryField) => fieldFromLibrary(libraryField, { parentId: itemObjectId }));

  // Sublimit/subretention types are scoped to a coverage type, so a new line starts
  // without any rather than inheriting another line's.
  selectedFields.value = [...selectedFields.value, itemObject, ...childFields];
};

const getStructuredArrayField = (spec: StructuredArraySpec, parentId?: string): JsonSchemaField | undefined =>
  selectedFields.value.find((f) => f.name === spec.arrayName && f.type === 'array' && f.parentId === parentId);

const getStructuredItems = (spec: StructuredArraySpec): JsonSchemaField[] =>
  selectedFields.value.filter((f) => f.name === spec.itemName);

const structuredLibraryFields = (spec: StructuredArraySpec) => fieldLibrary.value.filter((f) => f.category === spec.category && f.isActive);

const structuredChildrenFor = (itemObjectId: string, libraryFields: PromptField[]): JsonSchemaField[] => {
  const existing = selectedFields.value.filter((f) => f.parentId === itemObjectId);
  const existingNames = new Set(existing.map((f) => f.name));
  const existingSourceIds = new Set(existing.filter((f) => f.sourceField).map((f) => f.sourceField!.id));

  return libraryFields
    .filter((lf) => !existingNames.has(lf.fieldName) && !existingSourceIds.has(lf.id))
    .map((lf) => fieldFromLibrary(lf, { parentId: itemObjectId }));
};

const typedCoverageLineItems = computed(() => getCoverageLineItems().filter((item) => item.coverageTypeId));

const selectedSubitemLine = computed(() => typedCoverageLineItems.value.find((item) => item.id === selectedSubitemLineId.value));

const usedSubitemTypeIds = (spec: StructuredArraySpec, lineItemId: string): Set<number> => {
  const arrayField = getStructuredArrayField(spec, lineItemId);
  if (!arrayField) return new Set();
  return new Set(
    selectedFields.value
      .filter((f) => f.parentId === arrayField.id && f[spec.typeIdKey])
      .map((f) => f[spec.typeIdKey] as number),
  );
};

const availableSubitemTypes = computed(() => {
  const spec = activeSubitemSpec.value;
  const line = selectedSubitemLine.value;
  if (!spec || !line) return [];
  const used = usedSubitemTypeIds(spec, line.id);
  return subitemTypes.value[spec.kind].filter((t) => t.coverageTypeId === line.coverageTypeId && !used.has(t.id));
});

const canAddSubitems = (spec: StructuredArraySpec): boolean =>
  !props.disabled && structuredLibraryFields(spec).length > 0 && typedCoverageLineItems.value.length > 0;

const openSubitemSelector = (spec: StructuredArraySpec) => {
  activeSubitemSpec.value = spec;
  const lines = typedCoverageLineItems.value;
  selectedSubitemLineId.value = lines.length === 1 ? lines[0].id : '';
  selectedSubitemTypeId.value = '';
};

const cancelSubitemSelection = () => {
  activeSubitemSpec.value = null;
  selectedSubitemLineId.value = '';
  selectedSubitemTypeId.value = '';
};

const confirmSubitemSelection = () => {
  const spec = activeSubitemSpec.value;
  const line = selectedSubitemLine.value;
  const type = availableSubitemTypes.value.find((t) => t.id === selectedSubitemTypeId.value);
  if (!spec || !line || !type) return;

  addSubitemForType(spec, line.id, type);
  selectedSubitemTypeId.value = '';
};

// A typed item replaces any untyped legacy item in the same array: the API's union gate
// needs every item template to carry a type name.
const addSubitemForType = (spec: StructuredArraySpec, lineItemId: string, type: SubitemType) => {
  let fields = selectedFields.value;
  const additions: JsonSchemaField[] = [];

  let arrayField = getStructuredArrayField(spec, lineItemId);
  if (!arrayField) {
    arrayField = {
      id: generateFieldId(),
      name: spec.arrayName,
      description: spec.arrayDescription,
      type: 'array',
      required: false,
      parentId: lineItemId,
    };
    additions.push(arrayField);
  }

  const arrayId = arrayField.id;
  fields
    .filter((f) => f.parentId === arrayId && !isTypedItem(f))
    .forEach((legacy) => {
      fields = removeFieldAndDescendants(legacy.id, fields);
    });

  const itemObject: JsonSchemaField = {
    id: generateFieldId(),
    name: spec.itemName,
    description: spec.itemDescriptionFor(type.derivedName),
    type: 'object',
    required: false,
    parentId: arrayId,
    [spec.typeIdKey]: type.id,
    [spec.typeNameKey]: type.derivedName,
  };
  const childFields = structuredLibraryFields(spec).map((lf) => fieldFromLibrary(lf, { parentId: itemObject.id }));

  selectedFields.value = [...fields, ...additions, itemObject, ...childFields];
};

// Item nodes are created only through the type selector, so a library click on an empty
// category has nowhere to put the field.
const addStructuredField = (spec: StructuredArraySpec, libraryField: PromptField) => {
  const items = getStructuredItems(spec);
  if (items.length === 0) {
    console.warn(`Add a ${spec.itemLabel} type before adding "${libraryField.displayName}"`);
    return;
  }

  const additions = items.flatMap((item) => structuredChildrenFor(item.id, [libraryField]));
  if (additions.length === 0) {
    console.warn(`Field "${libraryField.displayName}" is already added to every ${spec.itemLabel} item`);
    return;
  }

  selectedFields.value = [...selectedFields.value, ...additions];
};

const isStructuredFieldSelected = (spec: StructuredArraySpec, libraryField: PromptField): boolean =>
  getStructuredItems(spec).some((item) =>
    selectedFields.value.some(
      (field) =>
        field.parentId === item.id && (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
    ),
  );

const isCoverageLineFieldSelected = (libraryField: PromptField): boolean => {
  const items = getCoverageLineItems();
  if (items.length === 0) {
    // Fall back to legacy single-item check
    const coverageLineItem = getCoverageLineItemField();
    if (!coverageLineItem) return false;
    return selectedFields.value.some(
      (field) =>
        field.parentId === coverageLineItem.id &&
        (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
    );
  }
  // Check if the field exists under any coverage line item
  return items.some((item) =>
    selectedFields.value.some(
      (field) =>
        field.parentId === item.id && (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
    ),
  );
};

// Check if a field is already selected
const isFieldSelected = (libraryField: PromptField): boolean => {
  if (libraryField.category === 'coverageLines') {
    return isCoverageLineFieldSelected(libraryField);
  }

  const spec = specForCategory(libraryField.category);
  if (spec) {
    return isStructuredFieldSelected(spec, libraryField);
  }

  return selectedFields.value.some(
    (field) => field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id),
  );
};

// -----
// Lifecycle
// -----
onMounted(async () => {
  await Promise.all([loadFieldLibrary(), loadCoverageTypes(), loadSubitemTypes()]);
  parseExistingSchema();
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);

  mobileMql = window.matchMedia('(max-width: 767px)');
  isMobileViewport.value = mobileMql.matches;
  mobileMql.addEventListener('change', onMobileChange);
});

const loadCoverageTypes = async () => {
  try {
    coverageTypes.value = await policyTypeService.GetDistinctCoverageTypes();
  } catch (error) {
    console.error('Failed to load coverage types:', error);
  }
};

const loadSubitemTypes = async () => {
  const load = async (kind: SubitemKind) => {
    try {
      subitemTypes.value[kind] = await subitemTypeService.list(kind);
    } catch (error) {
      console.error(`Failed to load ${kind} types:`, error);
    }
  };
  await Promise.all([load('sublimit'), load('subretention')]);
};

onUnmounted(() => {
  // Clear any pending debounce timeout
  if (editorUpdateTimeout) {
    clearTimeout(editorUpdateTimeout);
    editorUpdateTimeout = null;
  }

  if (highlightTimeout) {
    clearTimeout(highlightTimeout);
    highlightTimeout = null;
  }

  if (editorView.value) {
    editorView.value.destroy();
    editorView.value = null;
  }

  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', endDrag);
  window.removeEventListener('pointercancel', endDrag);
  if (isDragging.value) {
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }

  if (mobileMql) {
    mobileMql.removeEventListener('change', onMobileChange);
    mobileMql = null;
  }
});

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    // Skip if this is just our own emit coming back via v-model
    if (newValue === lastEmittedSchema) {
      return;
    }
    parseExistingSchema();
  },
);

// -----
// CodeMirror Helper Functions (defined early for use in watchers)
// -----

// Helper to get current editor content based on mode
const getCurrentEditorContent = (): string => {
  return showExamplePayload.value ? generateExamplePayload() : stripFieldIdMarkers(generateJsonSchema());
};

// Helper to update editor content
const updateEditorContent = (content: string) => {
  if (editorView.value) {
    const currentDoc = editorView.value.state.doc.toString();
    if (currentDoc !== content) {
      // Set flag to indicate this is a programmatic update
      isProgrammaticUpdate.value = true;

      editorView.value.dispatch({
        changes: { from: 0, to: editorView.value.state.doc.length, insert: content },
      });

      // Reset flag after the update is processed
      setTimeout(() => {
        isProgrammaticUpdate.value = false;
      }, 0);
    }
  }
};

// Watch for changes to selectedFields - emit updates and update editor
watch(
  selectedFields,
  () => {
    if (!isSyncingFromEditor.value) {
      const newSchema = stripFieldIdMarkers(generateJsonSchema());
      lastEmittedSchema = newSchema;
      emit('update:modelValue', newSchema);

      // Update editor content if it exists
      if (editorView.value) {
        const currentContent = getCurrentEditorContent();
        updateEditorContent(currentContent);
      }
    }
  },
  { deep: true },
);

// Recompute field ↔ line maps whenever the schema changes
watch(
  rawGeneratedJsonSchema,
  (rawJsonStr) => {
    if (!showExamplePayload.value) {
      const { fieldToLine, lineToField } = buildFieldLineMaps(rawJsonStr);
      fieldLineMap.value = fieldToLine;
      lineFieldMap.value = lineToField;
    }
  },
  { immediate: true },
);

// -----
// Hierarchy Utility Functions
// -----

/**
 * Get all root fields (fields with no parentId)
 */
const getRootFields = (fields: JsonSchemaField[]): JsonSchemaField[] => {
  return fields.filter((field) => !field.parentId);
};

/**
 * Get all children of a specific parent field
 */
const getChildrenOf = (parentId: string, fields: JsonSchemaField[]): JsonSchemaField[] => {
  return fields.filter((field) => field.parentId === parentId);
};

/**
 * Reorder a sibling group within selectedFields without disturbing other fields.
 * Walks the flat array once and replaces each matching sibling with the next
 * item from `newOrder`. Order of non-matching fields is preserved.
 */
const reorderSiblings = (parentId: string | null | undefined, newOrder: JsonSchemaField[]) => {
  const queue = [...newOrder];
  // Mirror getRootFields/getChildrenOf semantics: root = no parentId (null/undefined/empty).
  const matches = (f: JsonSchemaField) => (parentId == null ? !f.parentId : f.parentId === parentId);
  selectedFields.value = selectedFields.value.map((f) => (matches(f) ? (queue.shift() ?? f) : f));
};

/**
 * Get the full path of a field (e.g., "parent.child.grandchild")
 * Skips placeholder fields (names starting with '_') when building the path
 */
const getFieldPath = (fieldId: string, fields: JsonSchemaField[] = selectedFields.value): string => {
  const field = fields.find((f) => f.id === fieldId);
  if (!field) return '';

  // Skip placeholder fields - if this is a placeholder, get its parent's path instead
  if (field.name.startsWith('_')) {
    if (!field.parentId) return '';
    return getFieldPath(field.parentId, fields);
  }

  if (!field.parentId) return field.name;

  const parent = fields.find((f) => f.id === field.parentId);
  if (!parent) return field.name;

  // If parent is a placeholder, skip it and go to its parent
  if (parent.name.startsWith('_')) {
    if (!parent.parentId) return field.name;
    const grandParent = fields.find((f) => f.id === parent.parentId);
    if (!grandParent) return field.name;
    return `${getFieldPath(grandParent.id, fields)}.${field.name}`;
  }

  return `${getFieldPath(parent.id, fields)}.${field.name}`;
};

/**
 * Remove a field and all its descendants
 */
const removeFieldAndDescendants = (fieldId: string, fields: JsonSchemaField[]): JsonSchemaField[] => {
  // Get all descendants recursively
  const getDescendants = (parentId: string): JsonSchemaField[] => {
    const children = getChildrenOf(parentId, fields);
    const descendants: JsonSchemaField[] = [...children];
    children.forEach((child) => {
      descendants.push(...getDescendants(child.id));
    });
    return descendants;
  };

  const descendants = getDescendants(fieldId);
  const idsToRemove = new Set([fieldId, ...descendants.map((d) => d.id)]);
  return fields.filter((field) => !idsToRemove.has(field.id));
};

// -----
// Methods
// -----
const loadFieldLibrary = async () => {
  loading.value = true;
  try {
    fieldLibrary.value = await aiService.getAllPromptFields();
  } catch (error) {
    console.error('Failed to load field library:', error);
  } finally {
    loading.value = false;
  }
};

const parseExistingSchema = () => {
  isSyncingFromEditor.value = true;
  try {
    if (!props.modelValue) {
      selectedFields.value = [];
      return;
    }

    const schema = JSON.parse(props.modelValue) as Record<string, any>;
    selectedFields.value = parseSchemaToFields(schema);
  } catch (error) {
    console.error('Failed to parse existing schema:', error);
    selectedFields.value = [];
  } finally {
    // Reset flag on next tick so the async selectedFields watcher
    // still sees isSyncingFromEditor as true when it fires
    nextTick(() => {
      isSyncingFromEditor.value = false;
    });
  }
};

// Generate a unique field name by checking for existing names
const generateUniqueFieldName = (baseName: string, parentId?: string): string => {
  const siblings = parentId ? getChildrenOf(parentId, selectedFields.value) : getRootFields(selectedFields.value);

  const existingNames = siblings.map((f) => f.name);
  let counter = 1;
  let uniqueName = baseName;

  while (existingNames.includes(uniqueName)) {
    uniqueName = `${baseName}${counter}`;
    counter++;
  }

  return uniqueName;
};

const generateFieldId = () => {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ---------------------------
// Bidirectional Sync Methods
// ---------------------------

/**
 * Handle editor changes with debouncing
 */
const handleEditorUpdate = (update: ViewUpdate) => {
  // Skip if this is a programmatic update
  if (isProgrammaticUpdate.value) {
    return;
  }

  if (update.docChanged && !isSyncingFromEditor.value && !showExamplePayload.value) {
    // Clear any existing timeout
    if (editorUpdateTimeout) {
      clearTimeout(editorUpdateTimeout);
    }

    // Debounce the sync to avoid excessive parsing
    editorUpdateTimeout = setTimeout(() => {
      syncFromEditor();
    }, 500); // 500ms debounce
  }
};

/**
 * Sync changes from CodeMirror editor to selectedFields
 */
const syncFromEditor = () => {
  if (!editorView.value) return;

  const jsonString = editorView.value.state.doc.toString().trim();

  // Clear previous errors
  syncError.value = null;

  // Handle empty JSON - clear all fields
  if (!jsonString) {
    isSyncingFromEditor.value = true;
    selectedFields.value = [];
    lastEmittedSchema = '';
    emit('update:modelValue', '');
    nextTick(() => {
      isSyncingFromEditor.value = false;
    });
    return;
  }

  try {
    // Parse the JSON
    const schema = JSON.parse(jsonString) as Record<string, any>;

    // Prevent circular updates
    isSyncingFromEditor.value = true;

    // Update selectedFields with smart merge
    const newFields = parseSchemaToFields(schema);
    selectedFields.value = newFields;

    // The selectedFields watcher suppresses its emit while isSyncingFromEditor
    // is set, so emit here too — otherwise edits typed or pasted directly into
    // the editor never reach the parent v-model and are lost on save.
    const emittedSchema = stripFieldIdMarkers(generateJsonSchema());
    lastEmittedSchema = emittedSchema;
    emit('update:modelValue', emittedSchema);
  } catch (error) {
    console.error('[syncFromEditor] Failed to parse JSON:', error);
    syncError.value = error instanceof Error ? error.message : 'Invalid JSON';
  } finally {
    nextTick(() => {
      isSyncingFromEditor.value = false;
    });
  }
};

/**
 * Build the item node(s) under an array field. Descriptors that already carry explicit
 * item nodes are taken as-is; a legacy flattened array gets one synthesized, so it
 * re-serializes in the shape the API can read.
 */
const parseArrayItemsFromSchema = (arrayField: JsonSchemaField, properties: any[], processedFieldIds: Set<string>): JsonSchemaField[] => {
  const fields: JsonSchemaField[] = [];
  const explicitItems = properties.filter(isItemDescriptor);

  if (explicitItems.length > 0) {
    explicitItems.forEach((itemDef: any) => {
      const existingItem = selectedFields.value.find(
        (f) =>
          f.parentId === arrayField.id &&
          f.name === itemDef.name &&
          f.coverageTypeId === itemDef.coverageTypeId &&
          f.sublimitTypeId === itemDef.sublimitTypeId &&
          f.subretentionTypeId === itemDef.subretentionTypeId &&
          !processedFieldIds.has(f.id),
      );

      const itemObject: JsonSchemaField = {
        id: existingItem?.id || generateFieldId(),
        name: itemDef.name,
        description: itemDef.description || defaultItemDescription(itemDef),
        verificationLabel: itemDef.verificationLabel || undefined,
        type: 'object',
        required: itemDef.required === true,
        parentId: arrayField.id,
        sourceField: existingItem?.sourceField,
      };
      copyItemTypeKeys(itemObject, itemDef);

      processedFieldIds.add(itemObject.id);
      fields.push(itemObject);

      if (itemDef.properties && Array.isArray(itemDef.properties)) {
        fields.push(...parseChildFieldsFromArray(itemDef.properties, itemObject.id, processedFieldIds));
      }
    });

    return fields;
  }

  const placeholderName = itemNodeNameFor(arrayField.name);
  const existingPlaceholder = selectedFields.value.find((f) => f.parentId === arrayField.id && f.name === placeholderName);

  const placeholderChild: JsonSchemaField = {
    id: existingPlaceholder?.id || generateFieldId(),
    name: placeholderName,
    description: existingPlaceholder?.description || 'Array item object structure',
    type: 'object',
    required: false,
    parentId: arrayField.id,
    sourceField: existingPlaceholder?.sourceField,
  };

  processedFieldIds.add(placeholderChild.id);
  fields.push(placeholderChild);
  fields.push(...parseChildFieldsFromArray(properties, placeholderChild.id, processedFieldIds));

  return fields;
};

/**
 * Parse schema JSON into flat JsonSchemaField array (array-based format)
 * Preserves existing IDs where possible for bidirectional sync
 */
const parseSchemaToFields = (schema: any): JsonSchemaField[] => {
  const fields: JsonSchemaField[] = [];

  // Handle empty schema or non-array
  if (!schema || !Array.isArray(schema) || schema.length === 0) {
    return fields;
  }

  // Track which fields we've processed to avoid duplicates
  const processedFieldIds = new Set<string>();

  // Parse each field in the array
  schema.forEach((fieldDef) => {
    if (!fieldDef.name) return; // Skip invalid entries

    // Try to find existing field with same name to preserve ID and metadata.
    // Skip ids already claimed in this parse pass so duplicate-named siblings keep distinct ids.
    const existingField = selectedFields.value.find((f) => f.name === fieldDef.name && !f.parentId && !processedFieldIds.has(f.id));

    const field: JsonSchemaField = {
      id: existingField?.id || generateFieldId(),
      name: fieldDef.name,
      description: fieldDef.description || '',
      verificationLabel: fieldDef.verificationLabel || undefined,
      type: fieldDef.type || 'string',
      required: fieldDef.required === true,
      parentId: undefined, // Root field
      sourceField: existingField?.sourceField,
    };

    processedFieldIds.add(field.id);

    // Handle nested properties (array of fields)
    if (fieldDef.properties && Array.isArray(fieldDef.properties) && fieldDef.properties.length > 0) {
      if (fieldDef.type === 'object') {
        const childFields = parseChildFieldsFromArray(fieldDef.properties, field.id, processedFieldIds);
        fields.push(...childFields);
      } else if (fieldDef.type === 'array') {
        fields.push(...parseArrayItemsFromSchema(field, fieldDef.properties, processedFieldIds));
      }
    }

    fields.push(field);
  });

  return fields;
};

/**
 * Parse child fields recursively from array format into flat structure
 */
const parseChildFieldsFromArray = (properties: any[], parentId: string, processedFieldIds: Set<string>): JsonSchemaField[] => {
  const children: JsonSchemaField[] = [];

  // Handle empty properties
  if (!properties || properties.length === 0) {
    return children;
  }

  properties.forEach((childProp) => {
    if (!childProp.name) return; // Skip invalid entries

    // Find existing child with this name and parent.
    // Skip ids already claimed in this parse pass so duplicate-named siblings keep distinct ids.
    const existingChild = selectedFields.value.find((c) => c.name === childProp.name && c.parentId === parentId && !processedFieldIds.has(c.id));

    const child: JsonSchemaField = {
      id: existingChild?.id || generateFieldId(),
      name: childProp.name,
      description: childProp.description || '',
      verificationLabel: childProp.verificationLabel || undefined,
      type: childProp.type || 'string',
      required: childProp.required === true,
      parentId: parentId,
      sourceField: existingChild?.sourceField,
    };

    processedFieldIds.add(child.id);

    // Recursively handle nested properties
    if (childProp.properties && Array.isArray(childProp.properties) && childProp.properties.length > 0) {
      if (childProp.type === 'object') {
        const grandChildren = parseChildFieldsFromArray(childProp.properties, child.id, processedFieldIds);
        children.push(...grandChildren);
      } else if (childProp.type === 'array') {
        children.push(...parseArrayItemsFromSchema(child, childProp.properties, processedFieldIds));
      }
    }

    children.push(child);
  });

  return children;
};

const fieldFromLibrary = (libraryField: PromptField, overrides: Partial<JsonSchemaField> = {}): JsonSchemaField => ({
  id: generateFieldId(),
  name: libraryField.fieldName,
  description: libraryField.description,
  verificationLabel: libraryField.description || undefined,
  type: mapDataTypeToJsonType(libraryField.dataType),
  required: false,
  sourceField: libraryField,
  ...overrides,
});

const addFieldFromLibrary = (libraryField: PromptField) => {
  if (libraryField.category === 'coverageLines') {
    addCoverageLineField(libraryField);
    return;
  }

  const spec = specForCategory(libraryField.category);
  if (spec) {
    addStructuredField(spec, libraryField);
    return;
  }

  // Check if field already exists
  const existingField = selectedFields.value.find(
    (field) => field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id),
  );

  if (existingField) {
    // Could show a toast notification here
    console.warn(`Field "${libraryField.displayName}" is already added to the schema`);
    return;
  }

  selectedFields.value = [...selectedFields.value, fieldFromLibrary(libraryField)];
};

const mapDataTypeToJsonType = (dataType: string): 'string' | 'number' | 'boolean' => {
  switch (dataType) {
    case 'number':
    case 'decimal':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
};

const addCustomField = () => {
  const uniqueName = generateUniqueFieldName('newField');

  const newField: JsonSchemaField = {
    id: generateFieldId(),
    name: uniqueName,
    description: 'Custom field description',
    type: 'string',
    required: false,
  };

  selectedFields.value = [...selectedFields.value, newField];
};

const addAllVisibleFields = () => {
  const availableFields = filteredFields.value.filter((field) => !isFieldSelected(field));

  if (availableFields.length === 0) {
    console.warn('No available fields to add');
    return;
  }

  // Sublimit and subretention fields belong inside their item node, not at root.
  availableFields.forEach((f) => {
    const spec = specForCategory(f.category);
    if (spec) addStructuredField(spec, f);
  });

  const newFields: JsonSchemaField[] = availableFields
    .filter((libraryField) => !specForCategory(libraryField.category))
    .map((libraryField) => fieldFromLibrary(libraryField));

  selectedFields.value = [...selectedFields.value, ...newFields];

  console.log(`Added ${newFields.length} visible fields to schema`);
};

const addCoverageLineField = (libraryField: PromptField) => {
  const items = getCoverageLineItems();

  // If we have typed coverage line items, add the field to all of them
  if (items.length > 0 && items.some((i) => i.coverageTypeId)) {
    const newFields: JsonSchemaField[] = [];
    for (const item of items) {
      const exists = selectedFields.value.some(
        (f) => f.parentId === item.id && (f.name === libraryField.fieldName || (f.sourceField && f.sourceField.id === libraryField.id)),
      );
      if (!exists) {
        newFields.push(fieldFromLibrary(libraryField, { parentId: item.id }));
      }
    }
    if (newFields.length > 0) {
      selectedFields.value = [...selectedFields.value, ...newFields];
    }
    return;
  }

  // Legacy: single untyped _coverageLineItem
  const { itemObject } = ensureCoverageLineStructure();

  const existingField = selectedFields.value.find(
    (field) =>
      field.parentId === itemObject.id && (field.name === libraryField.fieldName || (field.sourceField && field.sourceField.id === libraryField.id)),
  );

  if (existingField) {
    console.warn(`Coverage line field "${libraryField.displayName}" is already added to the schema`);
    return;
  }

  selectedFields.value = [...selectedFields.value, fieldFromLibrary(libraryField, { parentId: itemObject.id })];
};

// Show the coverage type selector for adding coverage lines
const addCoverageLineArray = () => {
  const coverageLineFields = fieldLibrary.value.filter((f) => f.category === 'coverageLines' && f.isActive);

  if (coverageLineFields.length === 0) {
    console.warn('No coverage line fields found in library');
    return;
  }

  // If no coverage types loaded, fall back to legacy behavior (untyped)
  if (coverageTypes.value.length === 0) {
    addCoverageLineArrayLegacy();
    return;
  }

  showCoverageTypeSelector.value = true;
  selectedCoverageTypeId.value = '';
};

// Confirm coverage type selection and add the coverage line item
const confirmCoverageTypeSelection = () => {
  if (!selectedCoverageTypeId.value) return;

  const ct = coverageTypes.value.find((c) => c.coverageTypeId === selectedCoverageTypeId.value);
  if (!ct) return;

  addCoverageLineForType(ct.coverageTypeId, ct.coverageType);
  showCoverageTypeSelector.value = false;
  selectedCoverageTypeId.value = '';
};

const cancelCoverageTypeSelection = () => {
  showCoverageTypeSelector.value = false;
  selectedCoverageTypeId.value = '';
};

// Legacy behavior: add all coverage line fields under a single untyped _coverageLineItem
const addCoverageLineArrayLegacy = () => {
  const coverageLineFields = fieldLibrary.value.filter((f) => f.category === 'coverageLines' && f.isActive);
  const { itemObject } = ensureCoverageLineStructure();

  const existingFields = selectedFields.value.filter((field) => field.parentId === itemObject.id);
  const existingFieldNames = new Set(existingFields.map((field) => field.name));
  const existingSourceFieldIds = new Set(existingFields.filter((f) => f.sourceField).map((f) => f.sourceField!.id));

  const newFields: JsonSchemaField[] = coverageLineFields
    .filter((libraryField) => !existingFieldNames.has(libraryField.fieldName) && !existingSourceFieldIds.has(libraryField.id))
    .map((libraryField) => fieldFromLibrary(libraryField, { parentId: itemObject.id }));

  if (newFields.length === 0) {
    console.warn('All coverage line fields are already added to the schema');
    return;
  }

  selectedFields.value = [...selectedFields.value, ...newFields];
};

const copySchema = async () => {
  try {
    const contentToCopy = showExamplePayload.value ? generatedJsonSchema.value : examplePayload.value;
    await navigator.clipboard.writeText(contentToCopy);
    // Could add a toast notification here
  } catch (error) {
    console.error('Failed to copy schema:', error);
  }
};

const handleFormatOutput = () => {
  const output = editorView.value?.state.doc.toString() ?? '';
  let result = output;
  try {
    result = JSON.stringify(JSON.parse(output), null, 2);
  } catch (e) {
    console.error('Failed to format JSON:', e);
  }
  editorView.value?.dispatch({
    changes: { from: 0, to: editorView.value.state.doc.length, insert: result },
  });
};

const clearAllFields = () => {
  selectedFields.value = [];
};

// Nested field management methods - working with flat structure
// An array's fields belong to its item node. Creates the node when missing and adopts any
// fields sitting directly on the array, which would otherwise be dropped on serialize once
// an item node exists. Returns the item node's id.
const ensureArrayItemNode = (arrayField: JsonSchemaField): string => {
  const existing = selectedFields.value.find((f) => f.parentId === arrayField.id && isItemNode(f));
  if (existing) return existing.id;

  const itemNode: JsonSchemaField = {
    id: generateFieldId(),
    name: itemNodeNameFor(arrayField.name),
    description: 'Array item object structure',
    type: 'object',
    required: false,
    parentId: arrayField.id,
  };

  const strandedIds = new Set(
    selectedFields.value.filter((f) => f.parentId === arrayField.id && !f.name.startsWith('_')).map((f) => f.id),
  );

  selectedFields.value = [
    ...selectedFields.value.map((f) => (strandedIds.has(f.id) ? { ...f, parentId: itemNode.id } : f)),
    itemNode,
  ];

  return itemNode.id;
};

const addChildField = (parentId: string) => {
  const parent = selectedFields.value.find((f) => f.id === parentId);

  // Children of an array belong to its item node, not the array itself.
  const effectiveParentId = parent?.type === 'array' ? ensureArrayItemNode(parent) : parentId;
  const uniqueName = generateUniqueFieldName('newChildField', effectiveParentId);

  const newChild: JsonSchemaField = {
    id: generateFieldId(),
    name: uniqueName,
    description: 'Child field description',
    type: 'string',
    required: false,
    parentId: effectiveParentId,
  };

  selectedFields.value = [...selectedFields.value, newChild];

  // A non-array parent gaining children has to become an object to hold them.
  if (effectiveParentId === parentId) {
    selectedFields.value = selectedFields.value.map((field) => {
      if (field.id === parentId && field.type !== 'object') {
        return { ...field, type: 'object' as const };
      }
      return field;
    });
  }
};

const removeFieldWithChildren = (fieldId: string) => {
  // Remove the field and all its descendants using utility function
  selectedFields.value = removeFieldAndDescendants(fieldId, selectedFields.value);
};

// Remove all selected fields that correspond to the given library field.
// For coverage line fields this removes instances across every coverage line item.
const removeFieldFromLibrary = (libraryField: PromptField) => {
  const matches = (field: JsonSchemaField) =>
    field.name === libraryField.fieldName || !!(field.sourceField && field.sourceField.id === libraryField.id);

  // Structured fields live inside item nodes; remove the instances but leave the array
  // and item scaffolding in place.
  const isStructured = libraryField.category === 'coverageLines' || !!specForCategory(libraryField.category);

  const idsToRemove = isStructured
    ? selectedFields.value.filter((f) => f.parentId && matches(f)).map((f) => f.id)
    : selectedFields.value.filter(matches).map((f) => f.id);

  idsToRemove.forEach((id) => removeFieldWithChildren(id));
};

// Promote field - move child to root level
const promoteField = (childId: string) => {
  // Find the child field to promote
  const childToPromote = selectedFields.value.find((f) => f.id === childId);
  if (!childToPromote) {
    console.error('[promoteField] Child field not found:', childId);
    return;
  }

  // Move the child to root level (remove parentId)
  selectedFields.value = selectedFields.value.map((field) => {
    if (field.id === childId) {
      return { ...field, parentId: null };
    }
    return field;
  });
};

const isFieldNameDuplicatedInLevel = (fieldName: string, siblings: JsonSchemaField[]): boolean => {
  const target = fieldName.toLowerCase();
  return siblings.some((f) => f.name.toLowerCase() === target);
};

const updateField = (fieldId: string, updates: Partial<JsonSchemaField>) => {
  // Simple update - find field by ID and update it
  selectedFields.value = selectedFields.value.map((field) => {
    if (field.id === fieldId) {
      return { ...field, ...updates };
    }
    return field;
  });

  // Becoming an array means existing children are now item fields, so give them an item
  // node to live in — otherwise they serialize flat and the API keeps only the first.
  if (updates.type === 'array') {
    const field = selectedFields.value.find((f) => f.id === fieldId);
    if (field && getChildrenOf(fieldId, selectedFields.value).length > 0) {
      ensureArrayItemNode(field);
    }
  }
};

// Generate example payload with mock data (array-based format with value fields)
const generateExamplePayload = (): string => {
  if (selectedFields.value.length === 0) {
    return '';
  }

  const payload: any[] = [];

  // Only process root fields (fields with no parentId)
  const rootFields = getRootFields(selectedFields.value);
  rootFields.forEach((field) => {
    // Skip placeholder fields with names starting with '_'
    if (!field.name.startsWith('_')) {
      payload.push(buildExampleFieldDefinition(field));
    }
  });

  return JSON.stringify(payload, null, 2);
};

// Computed version for template usage
const examplePayload = computed(() => generateExamplePayload());

// Build example field definition with mock values (array-based format)
const buildExampleFieldDefinition = (field: JsonSchemaField): Record<string, any> => {
  const fieldDef: Record<string, any> = {
    name: field.name,
    type: field.type,
    description: field.description,
    required: field.required,
  };

  const children = getChildrenOf(field.id, selectedFields.value);

  if (field.type === 'object' && children.length > 0) {
    fieldDef.properties = [];
    children.forEach((child) => {
      if (!child.name.startsWith('_')) {
        fieldDef.properties.push(buildExampleFieldDefinition(child));
      }
    });
  } else if (field.type === 'array' && children.length > 0) {
    // Mirrors buildFieldDefinition so the example payload matches the emitted schema.
    const typedItems = children.filter(isTypedItem);
    const itemNodes = typedItems.length > 0 ? typedItems : children.filter((c) => isItemNode(c));

    if (itemNodes.length > 0) {
      fieldDef.properties = itemNodes.map((item) => {
        const itemDef: Record<string, any> = {
          name: item.name,
          type: 'object',
          description: item.description,
          required: item.required,
        };
        copyItemTypeKeys(itemDef, item);
        const itemChildren = getChildrenOf(item.id, selectedFields.value).filter((c) => !c.name.startsWith('_'));
        if (itemChildren.length > 0) {
          itemDef.properties = itemChildren.map((c) => buildExampleFieldDefinition(c));
        }
        return itemDef;
      });
    } else {
      fieldDef.properties = children.filter((c) => !c.name.startsWith('_')).map((c) => buildExampleFieldDefinition(c));
    }
  } else {
    // Leaf field - add example value
    fieldDef.value = generateMockValue(field);
  }

  return fieldDef;
};

// Generate mock value for a leaf field
const generateMockValue = (field: JsonSchemaField): any => {
  switch (field.type) {
    case 'string':
      return field.name.toLowerCase().includes('name')
        ? 'Sample Account Name'
        : field.name.toLowerCase().includes('description')
          ? 'Sample description text'
          : field.name.toLowerCase().includes('email')
            ? 'user@example.com'
            : field.name.toLowerCase().includes('url')
              ? 'https://example.com'
              : field.name.toLowerCase().includes('id')
                ? 'sample-id-123'
                : field.name.toLowerCase().includes('code')
                  ? 'ABC123'
                  : 'Sample text';
    case 'number':
      return field.name.toLowerCase().includes('limit')
        ? 1000000
        : field.name.toLowerCase().includes('amount')
          ? 50000
          : field.name.toLowerCase().includes('count')
            ? 25
            : field.name.toLowerCase().includes('rate')
              ? 0.05
              : field.name.toLowerCase().includes('percentage')
                ? 85
                : 1234;
    case 'boolean':
      return field.name.toLowerCase().includes('active')
        ? true
        : field.name.toLowerCase().includes('enabled')
          ? true
          : field.name.toLowerCase().includes('disabled')
            ? false
            : field.name.toLowerCase().includes('required')
              ? true
              : false;
    default:
      return 'Sample data';
  }
};

// -----
// Children as rendered in the tree. Mirrors buildFieldDefinition so the tree and the JSON
// pane stay structurally aligned — the scroll map pairs them line for line. Item nodes under
// an array are shown; placeholders under an object are unwrapped.
const getChildrenOfFiltered = (parentId: string): JsonSchemaField[] => {
  if (!parentId) {
    return [];
  }

  const parent = selectedFields.value.find((f) => f.id === parentId);
  const directChildren = getChildrenOf(parentId, selectedFields.value);
  const result: JsonSchemaField[] = [];
  const seenIds = new Set<string>();

  const push = (field: JsonSchemaField) => {
    if (!seenIds.has(field.id)) {
      seenIds.add(field.id);
      result.push(field);
    }
  };

  if (parent?.type === 'array') {
    const typedItems = directChildren.filter(isTypedItem);
    const itemNodes = typedItems.length > 0 ? typedItems : directChildren.filter((c) => isItemNode(c));

    if (itemNodes.length > 0) {
      itemNodes.forEach(push);
    } else {
      directChildren.filter((c) => !c.name.startsWith('_')).forEach(push);
    }

    return result;
  }

  directChildren.forEach((child) => {
    if (child.name.startsWith('_')) {
      getChildrenOf(child.id, selectedFields.value).forEach((grandChild) => {
        if (!grandChild.name.startsWith('_')) {
          push(grandChild);
        }
      });
    } else {
      push(child);
    }
  });

  return result;
};

// Helper to get actual siblings (for duplicate checking) - uses real parentId, not filtered
const getActualSiblings = (fieldId: string): JsonSchemaField[] => {
  const field = selectedFields.value.find((f) => f.id === fieldId);
  if (!field) {
    return [];
  }

  if (!field.parentId) {
    // Root level - get all root fields (excluding placeholders and the field itself)
    return getRootFields(selectedFields.value).filter((f) => !f.name.startsWith('_') && f.id !== fieldId);
  }

  // Get all fields with the same parentId (excluding the field itself)
  return selectedFields.value.filter((f) => f.parentId === field.parentId && f.id !== fieldId);
};

const errorCount = computed(() => {
  let count = 0;
  for (const field of selectedFields.value) {
    if (isTypedItem(field)) continue;
    if (field.name.startsWith('_')) continue;
    if (isFieldNameDuplicatedInLevel(field.name, getActualSiblings(field.id))) {
      count++;
    }
  }
  return count;
});

watch(errorCount, (count) => emit('update:errorCount', count), { immediate: true });

// Promoting a field out of a structured array would break its item template, so the
// Promote control is hidden anywhere beneath coverage lines, sublimits or subretentions.
const isUnderCoverageLines = (fieldId: string): boolean => {
  let current = selectedFields.value.find((f) => f.id === fieldId);
  while (current) {
    if (current.name === 'coverageLines' && !current.parentId) return true;
    if (current.type === 'array' && specForArrayName(current.name)) return true;
    current = current.parentId ? selectedFields.value.find((f) => f.id === current!.parentId) : undefined;
  }
  return false;
};

// --------------------------------
// Bidirectional Scroll Navigation
// --------------------------------
const expandToFieldId = ref<string | null>(null);
const highlightedFieldId = ref<string | null>(null);
let highlightTimeout: ReturnType<typeof setTimeout> | null = null;

const getAncestorIds = (fieldId: string): string[] => {
  const ids: string[] = [];
  let current = selectedFields.value.find((f) => f.id === fieldId);
  while (current?.parentId) {
    ids.push(current.parentId);
    current = selectedFields.value.find((f) => f.id === current!.parentId);
  }
  return ids;
};

const scrollToJson = async (fieldId: string) => {
  const mapping = fieldLineMap.value.get(fieldId);
  if (!mapping || !editorView.value) return;

  if (previewCollapsed.value) {
    previewCollapsed.value = false;
    await nextTick();
  }

  if (showExamplePayload.value) {
    showExamplePayload.value = false;
    await nextTick();
  }

  editorView.value.dispatch({
    effects: EditorView.scrollIntoView(mapping.charPos, { y: 'center' }),
    selection: { anchor: mapping.charPos, head: mapping.endCharPos },
  });
  editorView.value.focus();
};

const scrollToField = async (fieldId: string) => {
  expandToFieldId.value = fieldId;
  await nextTick();
  // Allow FieldItem watchers to expand ancestors
  await nextTick();

  const el = document.querySelector(`[data-field-id="${fieldId}"]`) as HTMLElement | null;
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    highlightedFieldId.value = fieldId;
    if (highlightTimeout) clearTimeout(highlightTimeout);
    highlightTimeout = setTimeout(() => {
      highlightedFieldId.value = null;
    }, 1500);
  }
  expandToFieldId.value = null;
};

// ------------------------------------
// CodeMirror Field Navigation Widgets
// ------------------------------------
let scrollToFieldFn = scrollToField;

class FieldNavWidget extends WidgetType {
  constructor(readonly fieldId: string) {
    super();
  }
  toDOM() {
    const btn = document.createElement('button');
    btn.className = 'cm-field-nav-btn';
    btn.title = 'Show in selected fields';
    btn.setAttribute('type', 'button');
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`;
    btn.style.cssText =
      'display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;margin-left:6px;border:1px solid #cbd5e1;border-radius:4px;background:#f8fafc;color:#64748b;cursor:pointer;vertical-align:middle;';
    const fieldId = this.fieldId;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollToFieldFn(fieldId);
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#e0f2fe';
      btn.style.borderColor = '#93c5fd';
      btn.style.color = '#2563eb';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#f8fafc';
      btn.style.borderColor = '#cbd5e1';
      btn.style.color = '#64748b';
    });
    return btn;
  }
  eq(other: FieldNavWidget) {
    return this.fieldId === other.fieldId;
  }
  ignoreEvent() {
    return false;
  }
}

const setFieldNavEffect = StateEffect.define<DecorationSet>();

const fieldNavStateField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decos, tr) {
    for (const e of tr.effects) {
      if (e.is(setFieldNavEffect)) return e.value;
    }
    return decos.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

const updateFieldNavDecorations = () => {
  if (!editorView.value || showExamplePayload.value) return;

  const decorations: Array<{ pos: number; widget: FieldNavWidget }> = [];
  const doc = editorView.value.state.doc;

  for (const [lineNum, fieldId] of lineFieldMap.value) {
    if (lineNum < doc.lines) {
      const line = doc.line(lineNum + 1); // CodeMirror lines are 1-indexed
      decorations.push({ pos: line.to, widget: new FieldNavWidget(fieldId) });
    }
  }

  decorations.sort((a, b) => a.pos - b.pos);
  const decoSet = Decoration.set(decorations.map((d) => Decoration.widget({ widget: d.widget, side: 1 }).range(d.pos)));

  editorView.value.dispatch({ effects: setFieldNavEffect.of(decoSet) });
};

// Update decorations when maps change
watch(lineFieldMap, () => {
  nextTick(() => updateFieldNavDecorations());
});

// Clear decorations in example payload mode
watch(showExamplePayload, (isExample) => {
  if (isExample && editorView.value) {
    editorView.value.dispatch({ effects: setFieldNavEffect.of(Decoration.none) });
  } else {
    nextTick(() => updateFieldNavDecorations());
  }
});

// Provide context for recursive FieldItem components (must be called during setup, not onMounted)
// -----
provide('fieldBuilderContext', {
  fieldTypeOptions,
  getFieldPath: (fieldId: string) => getFieldPath(fieldId, selectedFields.value),
  isFieldNameDuplicatedInLevel,
  getChildrenOf: (parentId: string) => getChildrenOf(parentId, selectedFields.value),
  getChildrenOfFiltered: (parentId: string) => getChildrenOfFiltered(parentId),
  getActualSiblings: (fieldId: string) => getActualSiblings(fieldId),
  isUnderCoverageLines,
  isItemNode,
  scrollToJson,
  showExamplePayload,
  expandToFieldId,
  highlightedFieldId,
  getAncestorIds,
});

// --------------------
// CodeMirror Watchers
// --------------------

// Initialize CodeMirror when editorRef is available
watch(editorRef, (ef) => {
  if (ef !== null) {
    const initialDoc = getCurrentEditorContent();

    // Create update listener for bidirectional sync
    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      handleEditorUpdate(update);
    });

    const editorTheme = EditorView.theme({
      '&': {
        fontSize: '13px',
      },
      '.cm-content': {
        fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace',
        lineHeight: '1.5',
      },
      '.cm-gutters': {
        fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '13px',
      },
    });

    const startState = EditorState.create({
      doc: initialDoc,
      extensions: [
        basicSetup,
        json(),
        linter(jsonParseLinter()),
        updateListener,
        fieldNavStateField,
        editorTheme,
        EditorView.editable.of(!props.disabled), // Respect the disabled prop
      ],
    });

    editorView.value = new EditorView({
      state: startState,
      parent: ef,
    });

    nextTick(() => updateFieldNavDecorations());
  }
});

// Watch for toggle changes to update editor content
watch(showExamplePayload, () => {
  if (!isSyncingFromEditor.value) {
    updateEditorContent(getCurrentEditorContent());
    syncError.value = null;
  }
});
</script>

<template>
  <div
    ref="splitContainerRef"
    :class="['relative flex min-h-0 flex-1 flex-col gap-4 md:flex-row', props.readonly ? '' : 'md:gap-0']">
    <!-- Left Column: Field Library and Selected Fields (hidden in readonly mode) -->
    <div
      v-if="!props.readonly"
      class="flex min-h-0 min-w-0 basis-full flex-col space-y-4 md:basis-(--left-basis) md:pr-2"
      :style="{ '--left-basis': previewCollapsed ? '100%' : `${leftPct}%` }">
      <p
        v-if="props.disabled"
        class="text-xs text-slate-500 italic">
        Editing is only allowed for draft prompts
      </p>

      <!-- Field Library Panel -->
      <Card class="shrink-0 border-blue-200">
        <CardHeader class="py-3">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <CardTitle
                class="flex gap-1 text-sm text-blue-700 hover:cursor-pointer"
                @click="isFieldLibraryCollapsed = !isFieldLibraryCollapsed">
                <button
                  type="button"
                  class="flex h-5 w-5 shrink-0 items-center justify-center rounded text-blue-600 hover:bg-blue-50"
                  :title="isFieldLibraryCollapsed ? 'Expand' : 'Collapse'">
                  <ChevronRight
                    v-if="isFieldLibraryCollapsed"
                    class="h-4 w-4" />
                  <ChevronDown
                    v-else
                    class="h-4 w-4" />
                </button>
                Field Library
              </CardTitle>
            </div>
            <Button
              size="sm"
              variant="outline"
              class="gap-1 border-blue-200 text-blue-600 hover:bg-blue-50"
              :disabled="props.disabled || loading || filteredFields.filter((f) => !isFieldSelected(f)).length === 0"
              @click.stop="addAllVisibleFields">
              <Plus class="h-3 w-3" />
              Add All Visible ({{ filteredFields.filter((f) => !isFieldSelected(f)).length }})
            </Button>
          </div>

          <!-- Search and Filter -->
          <div
            v-if="!isFieldLibraryCollapsed"
            class="mt-2 flex gap-2">
            <div class="relative min-w-0 flex-2">
              <Input
                v-model="searchTerm"
                placeholder="Search fields..."
                size="sm" />
              <button
                v-if="searchTerm"
                @click="searchTerm = ''"
                class="absolute top-1/2 right-2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                title="Clear search">
                <X class="h-4 w-4" />
              </button>
            </div>

            <div class="flex-1 shrink-0">
              <UISelect
                v-model="selectedCategory"
                :options="categorySelectOptions"
                size="sm"
                placeholder="All Categories" />
            </div>
          </div>
        </CardHeader>

        <CardContent
          v-if="!isFieldLibraryCollapsed"
          class="max-h-64 overflow-y-auto">
          <div
            v-if="loading"
            class="py-4 text-center text-sm text-slate-500">
            Loading fields...
          </div>

          <div
            v-else-if="filteredFields.length === 0"
            class="py-4 text-center text-sm text-slate-500">
            No fields found
          </div>

          <div
            v-else
            class="space-y-3">
            <div
              v-for="[category, fields] in fieldsByCategory"
              :key="category"
              class="space-y-2">
              <div class="flex items-center gap-2">
                <h5 class="text-xs font-semibold tracking-wide text-slate-600 uppercase">
                  {{ categoryLabels[category as FieldCategory] || category }}
                </h5>
              </div>

              <div class="space-y-1">
                <div
                  v-for="field in fields"
                  :key="field.id"
                  :class="[
                    'group flex cursor-pointer items-start justify-between rounded border p-2',
                    isFieldSelected(field)
                      ? 'border-green-200 bg-green-50 hover:border-red-200 hover:bg-red-50'
                      : 'border-slate-200 hover:bg-blue-50',
                  ]"
                  :title="isFieldSelected(field) ? 'Click to remove' : 'Click to add'"
                  @click="isFieldSelected(field) ? removeFieldFromLibrary(field) : addFieldFromLibrary(field)">
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span class="truncate text-xs font-medium text-slate-700">{{ field.displayName }}</span>
                      <span class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-slate-600 uppercase">
                        {{ field.dataType }}
                      </span>
                      <span
                        v-if="isFieldSelected(field)"
                        class="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 group-hover:hidden">
                        Added
                      </span>
                    </div>
                    <div class="truncate text-xs text-slate-500">{{ field.description }}</div>
                  </div>
                  <template v-if="isFieldSelected(field)">
                    <svg
                      class="mt-0.5 h-4 w-4 text-green-600 group-hover:hidden"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"></path>
                    </svg>
                    <X class="mt-0.5 hidden h-4 w-4 text-red-600 group-hover:block" />
                  </template>
                  <Plus
                    v-else
                    class="mt-0.5 h-4 w-4 text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Control Buttons -->
      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          class="gap-1 border-green-200 text-green-600 hover:bg-green-50"
          :disabled="props.disabled || fieldLibrary.filter((f) => f.category === 'coverageLines' && f.isActive).length === 0"
          @click="addCoverageLineArray">
          <Plus class="h-3 w-3" />
          Add Coverage Lines
        </Button>

        <Button
          size="sm"
          variant="outline"
          class="gap-1 border-purple-200 text-purple-600 hover:bg-purple-50"
          :disabled="!canAddSubitems(STRUCTURED_ARRAYS.sublimits)"
          :title="typedCoverageLineItems.length === 0 ? 'Add a coverage line first' : undefined"
          @click="openSubitemSelector(STRUCTURED_ARRAYS.sublimits)">
          <Plus class="h-3 w-3" />
          Add Sublimits
        </Button>

        <Button
          size="sm"
          variant="outline"
          class="gap-1 border-amber-200 text-amber-600 hover:bg-amber-50"
          :disabled="!canAddSubitems(STRUCTURED_ARRAYS.subretentions)"
          :title="typedCoverageLineItems.length === 0 ? 'Add a coverage line first' : undefined"
          @click="openSubitemSelector(STRUCTURED_ARRAYS.subretentions)">
          <Plus class="h-3 w-3" />
          Add Subretentions
        </Button>

        <!-- Coverage Type Selector (shown after clicking Add Coverage Lines) -->
        <div
          v-if="showCoverageTypeSelector"
          class="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-2">
          <UISelect
            :model-value="String(selectedCoverageTypeId)"
            :options="[
              { value: '', label: 'Select coverage type...' },
              ...availableCoverageTypes.map((ct) => ({ value: String(ct.coverageTypeId), label: ct.coverageType })),
            ]"
            class="min-w-[200px]"
            @update:model-value="selectedCoverageTypeId = $event ? Number($event) : ''" />
          <Button
            size="sm"
            variant="outline"
            class="border-green-300 text-green-700 hover:bg-green-100"
            :disabled="!selectedCoverageTypeId"
            @click="confirmCoverageTypeSelection">
            Add
          </Button>
          <Button
            size="sm"
            variant="ghost"
            class="text-gray-500 hover:text-gray-700"
            @click="cancelCoverageTypeSelection">
            <X class="h-3 w-3" />
          </Button>
        </div>

        <div
          v-if="activeSubitemSpec"
          data-testid="subitem-type-selector"
          class="flex items-center gap-2 rounded-md border p-2"
          :class="activeSubitemSpec.kind === 'sublimit' ? 'border-purple-200 bg-purple-50' : 'border-amber-200 bg-amber-50'">
          <UISelect
            v-if="typedCoverageLineItems.length > 1"
            :model-value="selectedSubitemLineId"
            :options="[
              { value: '', label: 'Select coverage line...' },
              ...typedCoverageLineItems.map((line) => ({ value: line.id, label: line.coverageTypeName ?? '' })),
            ]"
            class="min-w-[180px]"
            @update:model-value="
              selectedSubitemLineId = $event ?? '';
              selectedSubitemTypeId = '';
            " />
          <UISelect
            :model-value="String(selectedSubitemTypeId)"
            :options="[
              { value: '', label: `Select ${activeSubitemSpec.itemLabel} type...` },
              ...availableSubitemTypes.map((t) => ({ value: String(t.id), label: t.derivedName })),
            ]"
            :disabled="!selectedSubitemLine"
            class="min-w-[200px]"
            @update:model-value="selectedSubitemTypeId = $event ? Number($event) : ''" />
          <Button
            size="sm"
            variant="outline"
            :disabled="!selectedSubitemTypeId"
            @click="confirmSubitemSelection">
            Add
          </Button>
          <Button
            size="sm"
            variant="ghost"
            class="text-gray-500 hover:text-gray-700"
            @click="cancelSubitemSelection">
            <X class="h-3 w-3" />
          </Button>
        </div>

        <Button
          size="sm"
          variant="outline"
          class="gap-1"
          :disabled="props.disabled"
          @click="addCustomField">
          <Plus class="h-3 w-3" />
          Custom Field
        </Button>

        <Button
          size="sm"
          variant="outline"
          class="gap-1 border-red-200 text-red-600 hover:bg-red-50"
          :disabled="props.disabled || selectedFields.length === 0"
          @click="clearAllFields">
          <Trash2 class="h-3 w-3" />
          Clear All
        </Button>
      </div>

      <!-- Selected Fields -->
      <div
        v-if="selectedFields.length > 0"
        class="min-h-0 flex-1 space-y-2 overflow-auto px-0.5">
        <h5 class="text-sm font-medium text-slate-700">
          Selected Fields ({{ selectedFields.filter((f) => !f.name.startsWith('_') || isItemNode(f)).length }})
        </h5>

        <draggable
          :model-value="visibleRootFields"
          @update:model-value="(v: JsonSchemaField[]) => reorderSiblings(null, v)"
          group="siblings-root"
          item-key="id"
          handle=".field-drag-handle"
          :animation="150"
          class="space-y-1">
          <template #item="{ element: field }">
            <div>
              <FieldItem
                :field="field"
                :depth="0"
                :disabled="props.disabled"
                @add-child="addChildField"
                @remove-field="removeFieldWithChildren"
                @update-field="updateField"
                @promote-field="promoteField"
                @toggle-expansion="() => {}"
                @reorder-siblings="reorderSiblings" />
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <!-- Divider (editable mode, preview expanded) — desktop only -->
    <div
      v-if="!props.readonly && !previewCollapsed && !isMobileViewport"
      role="separator"
      aria-orientation="vertical"
      :aria-valuemin="MIN_LEFT_PCT"
      :aria-valuemax="MAX_LEFT_PCT"
      :aria-valuenow="Math.round(leftPct)"
      tabindex="0"
      :class="[
        'group relative hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center transition-colors md:flex',
        isDragging ? 'bg-blue-400' : 'bg-slate-200 hover:bg-blue-300',
      ]"
      title="Drag to resize · Double-click to reset"
      @pointerdown="startDrag"
      @keydown="onDividerKey"
      @dblclick="resetSplit">
      <GripVertical
        class="h-4 w-4 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100"
        :class="{ 'opacity-100': isDragging }" />
    </div>

    <!-- Collapsed-preview rail (editable mode, preview collapsed) — desktop only -->
    <button
      v-if="!props.readonly && previewCollapsed && !isMobileViewport"
      type="button"
      class="group hidden w-7 shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded border border-slate-200 bg-slate-50 py-3 text-slate-500 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 md:flex"
      title="Show JSON preview"
      @click="previewCollapsed = false">
      <ChevronLeft class="h-4 w-4" />
      <span class="rotate-180 text-xs font-medium tracking-wider whitespace-nowrap uppercase [writing-mode:vertical-rl]"> Preview </span>
      <ChevronLeft class="h-4 w-4" />
    </button>

    <!-- Right Column: JSON Preview (full width in readonly mode) -->
    <div
      v-if="props.readonly || !previewCollapsed || isMobileViewport"
      :class="['flex min-h-0 min-w-0 basis-full flex-col', props.readonly ? '' : 'md:basis-(--right-basis) md:pl-2']"
      :style="props.readonly ? {} : { '--right-basis': `${100 - leftPct}%` }">
      <!-- JSON Preview -->
      <div
        v-if="generatedJsonSchema"
        class="flex min-h-0 flex-1 flex-col space-y-2">
        <div class="flex shrink-0 flex-col gap-2">
          <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <h5 class="text-sm font-medium whitespace-nowrap text-slate-700">
                {{ showExamplePayload ? 'Example Output' : 'Prompt Schema' }}
              </h5>
              <span
                v-if="!showExamplePayload && !props.disabled"
                class="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-blue-700"
                title="Changes to the JSON will update the field builder">
                Editable
              </span>
            </div>
            <Button
              v-if="!props.readonly && !isMobileViewport"
              size="sm"
              variant="outline"
              title="Hide preview"
              @click="previewCollapsed = true">
              <ChevronRight class="h-2 w-2" />
              Hide
            </Button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Toggle
              id="example-payload-toggle"
              label="Show Example Output"
              v-model="showExamplePayload" />
            <Button
              size="sm"
              variant="outline"
              class="gap-1"
              @click="handleFormatOutput">
              <Braces class="h-3 w-3" />
              Format
            </Button>
            <Button
              size="sm"
              variant="outline"
              class="gap-1"
              @click="copySchema">
              <Copy class="h-3 w-3" />
              Copy
            </Button>
          </div>
        </div>

        <!-- Sync Error Alert -->
        <div
          v-if="syncError && !showExamplePayload"
          class="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm">
          <svg
            class="h-5 w-5 shrink-0 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="flex-1">
            <p class="font-medium text-red-800">JSON Sync Error</p>
            <p class="text-red-700">{{ syncError }}</p>
            <p class="mt-1 text-xs text-red-600">Fix the JSON syntax to sync changes back to the field builder.</p>
          </div>
          <button
            @click="syncError = null"
            class="shrink-0 text-red-400 hover:text-red-600"
            title="Dismiss">
            <X class="h-4 w-4" />
          </button>
        </div>

        <div
          ref="editorRef"
          class="min-h-0 flex-1 overflow-auto rounded border bg-slate-50"></div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="flex min-h-0 flex-1 flex-col">
        <div
          v-if="!props.readonly && !isMobileViewport"
          class="flex shrink-0 justify-end">
          <Button
            size="sm"
            variant="outline"
            class="gap-1"
            title="Hide preview"
            @click="previewCollapsed = true">
            <ChevronRight class="h-3 w-3" />
            Hide
          </Button>
        </div>
        <div class="flex flex-1 items-center justify-center">
          <div class="text-center text-sm text-slate-500">
            <div class="mb-2">{{ props.readonly ? 'No expected output defined' : 'No fields selected' }}</div>
            <div v-if="!props.readonly">Add fields from the library or create custom fields to define your expected JSON output</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
